import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import "./i18n";
import "./App.css";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Transactions = lazy(() => import("./pages/Transactions"));
const NewTransaction = lazy(() => import("./pages/NewTransaction"));
const TransactionDetail = lazy(() => import("./pages/TransactionDetail"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const PublicUpload = lazy(() => import("./pages/PublicUpload"));
const DocumentTracking = lazy(() => import("./pages/DocumentTracking"));
const ReportSettings = lazy(() => import("./pages/ReportSettings"));
const BatchReview = lazy(() => import("./pages/BatchReview"));
const PepRegister = lazy(() => import("./pages/PepRegister"));
const PepReviewQueue = lazy(() => import("./pages/PepReviewQueue"));
const PepLifecycleSettings = lazy(() => import("./pages/PepLifecycleSettings"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const ClientList = lazy(() => import("./pages/ClientList"));
const Billing = lazy(() => import("./pages/Billing"));
const MLRODashboard = lazy(() => import("./pages/MLRODashboard"));
const STRList = lazy(() => import("./pages/STRList"));
const STRDetail = lazy(() => import("./pages/STRDetail"));
const AuditReadiness = lazy(() => import("./pages/AuditReadiness"));
const ComplianceChecklist = lazy(() => import("./pages/ComplianceChecklist"));
const RegulatoryReports = lazy(() => import("./pages/RegulatoryReports"));
const PolicyAttestation = lazy(() => import("./pages/PolicyAttestation"));
const AuditLogSearch = lazy(() => import("./pages/AuditLogSearch"));
const TrainingLog = lazy(() => import("./pages/TrainingLog"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f8fafc" }}>
        <div className="w-8 h-8 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, isAdmin, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f8fafc" }}>
        <div className="w-8 h-8 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin()) return <Navigate to="/dashboard" replace />;
  return children;
};

// Every /api/billing/* endpoint is Depends(require_super_admin_billing), so a
// group admin who typed the URL got the page shell and a column of 403s.
// The sidebar has always declared /billing as super_admin only; this makes
// the route agree with both the sidebar and the API.
const SuperAdminRoute = ({ children }) => {
  const { user, isSuperAdmin, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f8fafc" }}>
        <div className="w-8 h-8 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!isSuperAdmin()) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ErrorBoundary>
        <Toaster position="top-right" richColors />
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center" style={{ background: "#f8fafc" }}>
            <div className="w-8 h-8 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin" />
          </div>
        }>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/upload/:token" element={<PublicUpload />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="transactions/new" element={<NewTransaction />} />
            <Route path="transactions/:id" element={<TransactionDetail />} />
            <Route path="document-tracking" element={<DocumentTracking />} />
            <Route path="report-settings" element={<ReportSettings />} />
            <Route path="batch-review" element={<BatchReview />} />
            <Route path="pep-register" element={<AdminRoute><PepRegister /></AdminRoute>} />
            <Route path="pep-review" element={<AdminRoute><PepReviewQueue /></AdminRoute>} />
            <Route path="pep-lifecycle-settings" element={<SuperAdminRoute><PepLifecycleSettings /></SuperAdminRoute>} />
            <Route path="client-register" element={<ClientList />} />
            <Route path="mlro-dashboard" element={<AdminRoute><MLRODashboard /></AdminRoute>} />
            <Route path="str-reports" element={<AdminRoute><STRList /></AdminRoute>} />
            <Route path="str-reports/:id" element={<AdminRoute><STRDetail /></AdminRoute>} />
            <Route path="audit-readiness" element={<AdminRoute><AuditReadiness /></AdminRoute>} />
            <Route path="compliance-checklist" element={<AdminRoute><ComplianceChecklist /></AdminRoute>} />
            <Route path="regulatory-reports" element={<AdminRoute><RegulatoryReports /></AdminRoute>} />
            <Route path="policy-attestation" element={<AdminRoute><PolicyAttestation /></AdminRoute>} />
            <Route path="audit-log-search" element={<AdminRoute><AuditLogSearch /></AdminRoute>} />
            <Route path="training-log" element={<AdminRoute><TrainingLog /></AdminRoute>} />
            <Route path="billing" element={<SuperAdminRoute><Billing /></SuperAdminRoute>} />
            <Route path="projects" element={<Projects />} />
            <Route path="projects/:id" element={<ProjectDetail />} />
            <Route
              path="admin"
              element={
                <AdminRoute>
                  <AdminPanel />
                </AdminRoute>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
